import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { routesConfig } from "../../config/routes";
import { SwitchViewMode } from "../../shared/enums/SwitchViewMode";
import { WizardStepIndex } from "../../shared/enums/WizardStep";
import { castHelper } from "../../shared/helpers/castHelper";
import { cookies } from "../../shared/helpers/cookies";
import { action } from "../../store/action";
import { useCartOptional } from "../../store/hooks/useCartOptional";
import { useCartSelectionsOptional } from "../../store/hooks/useCartSelectionsOptional";
import { useMachineTonnage } from "../../store/hooks/useMachineTonnage";
import { useMarket } from "../../store/hooks/useMarket";
import { useOptional } from "../../store/hooks/useOptional";
import { useProduct } from "../../store/hooks/useProduct";
import { MachineTonnage } from "../../store/interfaces/MachineTonnage";
import Optional from "../Optional/Optional";
import WizardContainer from "./Container/WizardContainer";
import "./OptionalStep.scss";

const OptionalStep = () => {
  const { t } = useTranslation(["optionalstep", "metrics"]);
  const dispatch = useDispatch();
  const market = useMarket();

  const { productCategory } = useProduct();
  const {
    isMachineFixingsSelected,
    isTiltrotatorsSelected,
    isToolsSelected,
    machineFixingType,
    toolsProductTypes,
  } = useOptional();
  const cartOptional = useCartOptional();
  const cartSelectionsOptional = useCartSelectionsOptional();
  const machineTonnage = useMachineTonnage();
  const cookieMarket = cookies.getMarket();
  if (!machineTonnage) {
    const routeMarket = cookieMarket ? cookieMarket : "en";
    window.location.href = routesConfig[routeMarket].machineTonnageStep;
  }
  useEffect(() => {
    if (!cartSelectionsOptional && cartOptional) {
      dispatch(action.copyCartOptionalToCartOptionalSelections());
    }
  }, [dispatch, cartOptional, cartSelectionsOptional]);

  useEffect(() => {
    dispatch(
      action.saveWizard({
        activeStepIndex: WizardStepIndex.Optional,
        enableNext: true,
      })
    );

    dispatch(action.saveIsLoadedFromSummary(false));
  }, [dispatch]);

  const getTonnage = (tonnage) => {
    if (market === "en-US") return castHelper.toKilogram(tonnage).toString();
    else return tonnage.toString();
  };

  const getMachineTonnageText = (
    machineTonnage: MachineTonnage,
    translatedUnit: string
  ): string => {
    if (!machineTonnage) {
      return "";
    }

    if (machineTonnage.switchViewMode === SwitchViewMode.MachineSearch) {
      return `${machineTonnage.machineModel.name}`;
    }

    return `${getTonnage(machineTonnage.tonnage)} ${translatedUnit}`;
  };

  const getTonnageText = () => {
    if (market === "en-US") return t("lb", { ns: "metrics" });
    else return t("tonnes", { ns: "metrics" });
  };

  const machineTonnageText = getMachineTonnageText(
    machineTonnage,
    getTonnageText()
  );

  return (
    <WizardContainer>
      <div>
        <div className="desktopWidth">
          <h1>{t("optionalstep")}</h1>
          {machineTonnage.switchViewMode === SwitchViewMode.Tonnage && (
            <p>
              {t("introweight")} {}
              {t("yourmachineweight")}{" "}
              <span className="optionalStepMachineTonnage">
                {machineTonnageText}
              </span>
              {"? "}
              <br></br>
              {t("introaftervalue")}
            </p>
          )}
          {machineTonnage.switchViewMode === SwitchViewMode.MachineSearch && (
            <p>
              {t("introweight")} {}
              {t("yourmachine")}{" "}
              <span className="optionalStepMachineTonnage">
                {machineTonnageText}
              </span>
              {". "}
              <br></br>
              {t("introaftervalue")}
            </p>
          )}
        </div>

        <Optional
          isMachineFixingsSelected={isMachineFixingsSelected}
          isNoneGrappleModuleSelected={
            cartSelectionsOptional?.isNoneGrappleModuleSelected
          }
          isToolsSelected={isToolsSelected}
          isTiltrotatorsSelected={isTiltrotatorsSelected}
          productCategory={productCategory}
          selectedGrappleModule={cartSelectionsOptional?.grappleModule}
          selectedMachineFixing={cartSelectionsOptional?.machineFixing}
          selectedMachineFixingType={machineFixingType}
          selectedTiltrotator={cartSelectionsOptional?.tiltrotator}
          selectedToolsProductItems={cartSelectionsOptional?.toolsProductItems}
          selectedToolsProductTypes={toolsProductTypes}
        />
      </div>
    </WizardContainer>
  );
};

export default OptionalStep;
